import {
  createSubmissionsRepository,
  createMarketFinderRepository,
} from "../repositories/submissions.repository";
import type {
  SubmissionsRepository,
  ClassCodeSearchResult,
} from "../repositories/submissions.repository";
import type {
  AgentOriginThreadHydrationResult,
  Submission,
  MarketFinderApplicationRead,
  SubmissionUpdatePayload,
  GetSubmissionsParams,
  SubmissionsResponse,
  ScheduleOfHazard,
  BuildingData,
  BuildingPayload,
  CoverLetter,
  CoverLetterRevision,
  CommunicationReviewPage,
  ReviewGroup,
  ReviewGroupResolveRequest,
  CommunicationsSummary,
  ConversationThread,
  ConversationThreadMessage,
  ConversationUnreadState,
  ConversationMessageEmail,
  ConversationReplyAttachmentRef,
  EmailDocumentRead,
  MarketFinderUserPreferences,
  MarketFinderUserPreferencesUpdate,
  AimCompanyPage,
  AimContactImportInput,
  AimContactImportResult,
  AimContactPage,
  AimMarketImportInput,
  MarketFinderActorInfo,
  MarketContact,
  MarketContactDirectoryParams,
  MarketContactDirectoryResponse,
  MarketContactInput,
  MarketCreateInput,
  MarketCreateResponse,
  MarketDirectoryItem,
  MarketsDirectoryParams,
  MarketsDirectoryResponse,
  MarketUpdateInput,
  MarketPacket,
  MarketSuggestionResult,
  MarketPacketPreview,
  MarketPacketSendResult,
  MarketPacketUpdateInput,
  AgentEmailInput,
  MarketEmailInput,
  CommunicationMailbox,
  ConversationReplyDefaults,
  ConversationReplyRecipients,
  MarketOutboundEmailEdit,
  MarketOutboundEmailRead,
  EmailRecipients,
  MailTip,
  PremiseData,
  ApplicationLossRun,
  ApplicationLossPeriod,
  ApplicationLossPeriodUpdate,
  ApplicationLossClaim,
  ApplicationLossClaimUpdate,
  CoverageMetadata,
  ApplicationCoverageData,
  CoverageDataUpdate,
} from "../types/submissions.types";
import type { AuthHeaders } from "@/lib/auth/types";
import type { RenewalInformationInput } from "../validators/renewal.validator";
import type { HitRateResponse } from "../types/hit-rates.types";
import type { InspectionComparison } from "../types/inspection.types";
import type { ReprocessQueued } from "../types/submissions.types";
import type {
  ActionDraftPatch,
  AccountAnswer,
  AccountAnswerCorrection,
  AccountAnswerCreate,
  ConversationInterpretation,
  GroupDraft,
  MarketQuestion,
  MessageFact,
  MessageFactCorrection,
  ProposedAction,
  ProposedActionsResponse,
  Relay,
  RelayInput,
} from "../types/interpretation.types";
import type {
  ClassCodeInput,
  GLCoverageInput,
} from "../validators/general-liability.validator";

export class SubmissionsService {
  private static instance: SubmissionsService;
  private static marketFinderInstance: SubmissionsService;

  private constructor(
    private repository: SubmissionsRepository = createSubmissionsRepository(),
  ) {}

  static getInstance(): SubmissionsService {
    if (!SubmissionsService.instance) {
      SubmissionsService.instance = new SubmissionsService();
    }
    return SubmissionsService.instance;
  }

  static getMarketFinderInstance(): SubmissionsService {
    if (!SubmissionsService.marketFinderInstance) {
      SubmissionsService.marketFinderInstance = new SubmissionsService(
        createMarketFinderRepository(),
      );
    }
    return SubmissionsService.marketFinderInstance;
  }

  async getSubmissions(
    params: GetSubmissionsParams,
  ): Promise<SubmissionsResponse> {
    const response = await this.repository.getSubmissions(params);

    return {
      ...response,
      items: response.items.map(this.enrichSubmission),
    };
  }

  async getDistinctUnderwriters(params: {
    office_ids?: number[];
    company_ids?: number[];
    show_archived?: boolean;
    headers: AuthHeaders;
  }): Promise<{ id: string; displayName: string; self?: boolean }[]> {
    return this.repository.getDistinctUnderwriters(params);
  }

  async getSubmissionById(
    id: number,
    headers: AuthHeaders,
  ): Promise<Submission> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.getSubmissionById(id, headers);
    return this.enrichSubmission(submission);
  }

  async getMarketFinderApplicationById(
    id: number,
    headers: AuthHeaders,
  ): Promise<MarketFinderApplicationRead> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.getMarketFinderApplicationById(
      id,
      headers,
    );
    return this.enrichSubmission(submission);
  }

  async batchAssign(
    applicationIds: number[],
    companyId: number,
    officeId: number | null,
    headers: AuthHeaders,
  ): Promise<{ updated_count: number }> {
    if (applicationIds.length === 0) {
      throw new Error("No submissions selected");
    }
    return this.repository.batchAssign(
      applicationIds,
      companyId,
      officeId,
      headers,
    );
  }

  async archiveSubmission(
    id: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.archiveSubmission(id, true, headers);
  }

  async unarchiveSubmission(
    id: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.unarchiveSubmission(id, headers);
  }

  async archiveMultipleSubmissions(
    ids: number[],
    headers: AuthHeaders,
  ): Promise<void> {
    if (ids.length === 0) {
      throw new Error("No submission IDs provided");
    }

    await Promise.all(
      ids.map((id) => this.repository.archiveSubmission(id, true, headers)),
    );
  }

  async unarchiveMultipleSubmissions(
    ids: number[],
    headers: AuthHeaders,
  ): Promise<void> {
    if (ids.length === 0) {
      throw new Error("No submission IDs provided");
    }

    await Promise.all(
      ids.map((id) => this.repository.unarchiveSubmission(id, headers)),
    );
  }

  async addClassCode(
    submissionId: number,
    data: ClassCodeInput,
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (!data.class_code || !data.description) {
      throw new Error("Class code and description are required");
    }

    const response = await this.repository.addClassCode(
      submissionId,
      data,
      headers,
    );
    return this.enrichClassCode(response);
  }

  async addClassCodesBatch(
    submissionId: number,
    existingCodes: ScheduleOfHazard[],
    newCodes: ClassCodeInput[],
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard[]> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (newCodes.length === 0) {
      throw new Error("At least one class code is required");
    }

    for (const item of newCodes) {
      if (!item.class_code || !item.description) {
        throw new Error(
          "Class code and description are required for all items",
        );
      }
    }

    // Convert new codes to ScheduleOfHazard format
    const newHazards = newCodes.map((item, index) => ({
      class_code: item.class_code,
      classification: item.description,
      exposure: item.payroll || 0,
      premium_basis: item.premium_basis || "P",
      exposure_unit: "per $1000",
      location_number: item.location_number,
      hazard_number: existingCodes.length + index + 1,
    })) as ScheduleOfHazard[];

    // Combine existing + new
    const allHazards = [...existingCodes, ...newHazards];

    // PATCH with complete array
    const response = await this.repository.updateGeneralLiabilitySchedule(
      submissionId,
      allHazards,
      headers,
    );

    return response.schedule_of_hazard.map((code) =>
      this.enrichClassCode(code),
    );
  }

  async updateClassCode(
    submissionId: number,
    classCodeId: number,
    data: ClassCodeInput,
    currentCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard[]> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (classCodeId < 1) {
      throw new Error("Invalid class code ID");
    }

    // PATCH with complete array (multi-rater pattern)
    const response = await this.repository.updateClassCode(
      submissionId,
      classCodeId,
      data,
      currentCodes,
      headers,
    );

    return response.schedule_of_hazard.map((code) =>
      this.enrichClassCode(code),
    );
  }

  async deleteClassCode(
    submissionId: number,
    classCodeId: number,
    currentCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard[]> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (classCodeId < 1) {
      throw new Error("Invalid class code ID");
    }

    // Filter out the code to delete
    const remainingCodes = currentCodes.filter(
      (code) => code.id !== classCodeId,
    );

    // PATCH with remaining codes (multi-rater pattern)
    const response = await this.repository.deleteClassCode(
      submissionId,
      remainingCodes,
      headers,
    );

    return response.schedule_of_hazard.map((code) =>
      this.enrichClassCode(code),
    );
  }

  async updateInsuredInformation(
    submissionId: string,
    data: SubmissionUpdatePayload,
    headers: AuthHeaders,
  ): Promise<Submission> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.updateSubmission(id, data, headers);
    return this.enrichSubmission(submission);
  }

  async updateRenewalInformation(
    submissionId: string,
    data: RenewalInformationInput,
    headers: AuthHeaders,
  ): Promise<Submission> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.updateSubmission(id, data, headers);
    return this.enrichSubmission(submission);
  }

  async updateGLCoverage(
    submissionId: string,
    data: GLCoverageInput & { inferred_fields?: unknown[] },
    headers: AuthHeaders,
  ): Promise<void> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.updateGLCoverage(id, data, headers);
  }

  async updateGLFields(
    submissionId: string,
    data: Record<string, unknown>,
    headers: AuthHeaders,
  ): Promise<void> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }
    await this.repository.updateGLPartial(id, data, headers);
  }

  async getAllClassCodes(): Promise<ClassCodeSearchResult[]> {
    return this.repository.getAllClassCodes();
  }

  async getAllPropertyClassCodes(): Promise<
    Array<{ code: string; description: string }>
  > {
    return this.repository.getAllPropertyClassCodes();
  }

  async searchClassCodes(
    query: string,
    limit: number = 50,
  ): Promise<ClassCodeSearchResult[]> {
    if (limit < 1 || limit > 50) {
      throw new Error("Limit must be between 1 and 50");
    }

    return this.repository.searchClassCodes(query, limit);
  }

  async createPremise(
    submissionId: number,
    data: { full_address: string; description_of_operations?: string },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    return this.repository.createPremise(submissionId, data, headers);
  }

  async updatePremise(
    submissionId: number,
    premiseId: number,
    data: {
      full_address?: string;
      description_of_operations?: string;
      nature_of_business?: string;
      location_number?: number;
    },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    return this.repository.updatePremise(
      submissionId,
      premiseId,
      data,
      headers,
    );
  }

  async deletePremise(
    submissionId: number,
    premiseId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.deletePremise(submissionId, premiseId, headers);
  }

  async createBuilding(
    submissionId: number,
    premiseId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }
    if (premiseId < 1) {
      throw new Error("Invalid premise ID");
    }

    return this.repository.createBuilding(
      submissionId,
      premiseId,
      data,
      headers,
    );
  }

  async deleteBuilding(
    submissionId: number,
    buildingId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }
    if (buildingId < 1) {
      throw new Error("Invalid building ID");
    }

    await this.repository.deleteBuilding(submissionId, buildingId, headers);
  }

  async updateBuilding(
    submissionId: number,
    buildingId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (buildingId < 1) {
      throw new Error("Invalid building ID");
    }

    return this.repository.updateBuilding(
      submissionId,
      buildingId,
      data,
      headers,
    );
  }

  // Cover letter methods

  async generateCoverLetter(
    appId: number,
    data: { carrier_id?: string; carrier_name?: string; recipient_email?: string },
  ): Promise<CoverLetter> {
    return this.repository.generateCoverLetter(appId, data);
  }

  async getCoverLetters(appId: number): Promise<CoverLetter[]> {
    return this.repository.getCoverLetters(appId);
  }

  async getCoverLetter(appId: number, clId: number): Promise<CoverLetter> {
    return this.repository.getCoverLetter(appId, clId);
  }

  async updateCoverLetter(
    appId: number,
    clId: number,
    data: {
      subject?: string;
      body_html?: string;
      recipient_email?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: "improve" | "formal" | "concise" | "expand";
    },
  ): Promise<CoverLetter> {
    return this.repository.updateCoverLetter(appId, clId, data);
  }

  async aiAssist(data: {
    text: string;
    action: "improve" | "formal" | "concise" | "expand";
    app_id?: number;
    cover_letter_id?: number;
  }): Promise<{ text: string }> {
    return this.repository.aiAssist(data);
  }

  async getMarketFinderUserPreferences(): Promise<MarketFinderUserPreferences> {
    return this.repository.getMarketFinderUserPreferences();
  }

  async updateMarketFinderUserPreferences(
    data: MarketFinderUserPreferencesUpdate,
  ): Promise<MarketFinderUserPreferences> {
    return this.repository.updateMarketFinderUserPreferences(data);
  }

  async getHitRates(classCodes: string[]): Promise<HitRateResponse> {
    return this.repository.getHitRates(classCodes);
  }

  async getPackets(appId: number): Promise<MarketPacket[]> {
    return this.repository.getPackets(appId);
  }

  async getLossRun(appId: number): Promise<ApplicationLossRun> {
    return this.repository.getLossRun(appId);
  }

  async getInspection(id: number): Promise<InspectionComparison> {
    return this.repository.getInspection(id);
  }

  async reprocess(id: number, headers: AuthHeaders): Promise<ReprocessQueued> {
    return this.repository.reprocess(id, headers);
  }

  async getCoverageCatalog(): Promise<CoverageMetadata[]> {
    return this.repository.getCoverageCatalog();
  }

  async replaceRequestedCoverages(
    appId: number,
    coverages: string[],
    confirmPacketChanges = false,
    headers?: AuthHeaders,
  ): Promise<Submission> {
    return this.repository.replaceRequestedCoverages(
      appId,
      coverages,
      confirmPacketChanges,
      headers,
    );
  }

  async getCoverageData(appId: number): Promise<ApplicationCoverageData> {
    return this.repository.getCoverageData(appId);
  }

  async createCoverageData(
    appId: number,
    family: string,
    data: CoverageDataUpdate & { coverage_code?: string; premise_id?: number },
  ): Promise<ApplicationCoverageData> {
    return this.repository.createCoverageData(appId, family, data);
  }

  async updateCoverageData(
    appId: number,
    family: string,
    rowId: number,
    data: CoverageDataUpdate,
  ): Promise<ApplicationCoverageData> {
    return this.repository.updateCoverageData(appId, family, rowId, data);
  }

  async updateCoverageDataItem(
    appId: number,
    itemType: string,
    rowId: number,
    data: CoverageDataUpdate,
  ): Promise<ApplicationCoverageData> {
    return this.repository.updateCoverageDataItem(appId, itemType, rowId, data);
  }

  async createCoverageDataItem(
    appId: number,
    itemType: string,
    parentId: number,
    data: CoverageDataUpdate = {},
  ): Promise<ApplicationCoverageData> {
    return this.repository.createCoverageDataItem(
      appId,
      itemType,
      parentId,
      data,
    );
  }

  async updateLossPeriod(
    appId: number,
    periodId: number,
    data: ApplicationLossPeriodUpdate,
  ): Promise<ApplicationLossPeriod> {
    return this.repository.updateLossPeriod(appId, periodId, data);
  }

  async updateLossClaim(
    appId: number,
    claimId: number,
    data: ApplicationLossClaimUpdate,
  ): Promise<ApplicationLossClaim> {
    return this.repository.updateLossClaim(appId, claimId, data);
  }

  async getMarketSuggestions(appId: number): Promise<MarketSuggestionResult> {
    return this.repository.getMarketSuggestions(appId);
  }

  async refreshMarketSuggestions(appId: number): Promise<MarketSuggestionResult> {
    return this.repository.refreshMarketSuggestions(appId);
  }

  async createPacket(
    appId: number,
    data: { label?: string; coverages: string[] },
  ): Promise<MarketPacket> {
    return this.repository.createPacket(appId, data);
  }

  async updatePacket(
    appId: number,
    packetId: number,
    data: MarketPacketUpdateInput,
  ): Promise<MarketPacket> {
    return this.repository.updatePacket(appId, packetId, data);
  }

  async deletePacket(appId: number, packetId: number): Promise<void> {
    return this.repository.deletePacket(appId, packetId);
  }

  async setPacketAttachments(
    appId: number,
    packetId: number,
    recordIds: number[],
  ): Promise<MarketPacket> {
    return this.repository.setPacketAttachments(appId, packetId, recordIds);
  }

  async setPacketRecipients(
    appId: number,
    packetId: number,
    recipients: Array<{ carrier_id: string; contact_id: number }>,
  ): Promise<MarketPacket> {
    return this.repository.setPacketRecipients(appId, packetId, recipients);
  }

  async generatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      current_body_html?: string;
      format?: "standard" | "concise" | "detailed";
    } = {},
  ): Promise<CoverLetter> {
    return this.repository.generatePacketCoverLetter(appId, packetId, data);
  }

  async updatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      subject?: string;
      body_html?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: string;
    },
  ): Promise<CoverLetter> {
    return this.repository.updatePacketCoverLetter(appId, packetId, data);
  }

  async createNewPacketCoverLetter(
    appId: number,
    packetId: number,
  ): Promise<CoverLetter> {
    return this.repository.createNewPacketCoverLetter(appId, packetId);
  }

  async getPacketCoverLetterRevisions(
    appId: number,
    packetId: number,
  ): Promise<CoverLetterRevision[]> {
    return this.repository.getPacketCoverLetterRevisions(appId, packetId);
  }

  async previewPacket(appId: number, packetId: number): Promise<MarketPacketPreview> {
    return this.repository.previewPacket(appId, packetId);
  }

  async sendPacket(appId: number, packetId: number): Promise<MarketPacketSendResult> {
    return this.repository.sendPacket(appId, packetId);
  }

  async sendAgentEmail(
    appId: number,
    data: AgentEmailInput,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.sendAgentEmail(appId, data);
  }

  async listCommunicationMailboxes(appId: number): Promise<CommunicationMailbox[]> {
    return this.repository.listCommunicationMailboxes(appId);
  }

  async sendMarketEmail(
    appId: number,
    conversationId: number,
    data: MarketEmailInput,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.sendMarketEmail(appId, conversationId, data);
  }

  async listOutboundEmails(
    appId: number,
    conversationId?: number,
  ): Promise<MarketOutboundEmailRead[]> {
    return this.repository.listOutboundEmails(appId, conversationId);
  }

  async retryOutboundEmail(
    appId: number,
    outboundEmailId: number,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.retryOutboundEmail(appId, outboundEmailId);
  }

  async editOutboundEmail(
    appId: number,
    outboundEmailId: number,
    data: MarketOutboundEmailEdit,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.editOutboundEmail(appId, outboundEmailId, data);
  }

  async uploadAgentEmailAttachments(
    appId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return this.repository.uploadAgentEmailAttachments(appId, formData);
  }

  async getCommunicationsSummary(
    underwriterIds?: string[],
  ): Promise<CommunicationsSummary> {
    return this.repository.getCommunicationsSummary(underwriterIds);
  }

  async listApplicationConversations(
    appId: number,
  ): Promise<ConversationThread[]> {
    return this.repository.listApplicationConversations(appId);
  }

  async listEmailDocuments(appId: number): Promise<EmailDocumentRead[]> {
    return this.repository.listEmailDocuments(appId);
  }

  async hydrateAgentOriginThread(
    appId: number,
  ): Promise<AgentOriginThreadHydrationResult> {
    return this.repository.hydrateAgentOriginThread(appId);
  }

  async listConversationMessages(
    appId: number,
    conversationId: number,
  ): Promise<ConversationThreadMessage[]> {
    return this.repository.listConversationMessages(appId, conversationId);
  }

  async retryConversationMessageInterpretation(
    appId: number,
    messageId: number,
  ): Promise<ConversationThreadMessage> {
    return this.repository.retryConversationMessageInterpretation(appId, messageId);
  }

  async getConversationInterpretation(
    appId: number,
    conversationId: number,
  ): Promise<ConversationInterpretation> {
    return this.repository.getConversationInterpretation(appId, conversationId);
  }

  async dismissConversationFact(
    appId: number,
    factId: number,
  ): Promise<MessageFact> {
    return this.repository.dismissConversationFact(appId, factId);
  }

  async correctConversationFact(
    appId: number,
    factId: number,
    correction: MessageFactCorrection,
  ): Promise<MessageFact> {
    return this.repository.correctConversationFact(appId, factId, correction);
  }

  async listQuestions(
    appId: number,
    conversationId?: number,
  ): Promise<MarketQuestion[]> {
    return this.repository.listQuestions(appId, conversationId);
  }

  async listAccountAnswers(appId: number): Promise<AccountAnswer[]> {
    return this.repository.listAccountAnswers(appId);
  }

  async createAccountAnswer(
    appId: number,
    payload: AccountAnswerCreate,
  ): Promise<AccountAnswer> {
    return this.repository.createAccountAnswer(appId, payload);
  }

  async correctAccountAnswer(
    appId: number,
    answerId: number,
    correction: AccountAnswerCorrection,
  ): Promise<AccountAnswer> {
    return this.repository.correctAccountAnswer(appId, answerId, correction);
  }

  async deleteAccountAnswer(appId: number, answerId: number): Promise<void> {
    return this.repository.deleteAccountAnswer(appId, answerId);
  }

  async confirmAccountAnswerLink(appId: number, linkId: number): Promise<MarketQuestion> {
    return this.repository.confirmAccountAnswerLink(appId, linkId);
  }

  async dismissAccountAnswerLink(appId: number, linkId: number): Promise<MarketQuestion> {
    return this.repository.dismissAccountAnswerLink(appId, linkId);
  }

  async correctQuestionText(
    appId: number,
    questionId: number,
    text: string,
  ): Promise<MarketQuestion> {
    return this.repository.correctQuestionText(appId, questionId, text);
  }

  async reopenQuestion(appId: number, questionId: number): Promise<MarketQuestion> {
    return this.repository.reopenQuestion(appId, questionId);
  }

  async closeQuestion(appId: number, questionId: number): Promise<MarketQuestion> {
    return this.repository.closeQuestion(appId, questionId);
  }

  async confirmAnswerLink(
    appId: number,
    linkId: number,
    partial: boolean,
  ): Promise<MarketQuestion> {
    return this.repository.confirmAnswerLink(appId, linkId, partial);
  }

  async dismissAnswerLink(appId: number, linkId: number): Promise<MarketQuestion> {
    return this.repository.dismissAnswerLink(appId, linkId);
  }

  async createRelay(appId: number, relay: RelayInput): Promise<MarketOutboundEmailRead> {
    return this.repository.createRelay(appId, relay);
  }

  async listRelays(appId: number, conversationId?: number): Promise<Relay[]> {
    return this.repository.listRelays(appId, conversationId);
  }

  async listProposedActions(
    appId: number,
    conversationId?: number,
    messageId?: number,
  ): Promise<ProposedActionsResponse> {
    return this.repository.listProposedActions(appId, conversationId, messageId);
  }

  async editActionDraft(
    appId: number,
    draftId: number,
    patch: ActionDraftPatch,
  ): Promise<ProposedAction> {
    return this.repository.editActionDraft(appId, draftId, patch);
  }

  async approveProposedAction(
    appId: number,
    actionId: number,
    clientRequestId: string,
    attachments?: ConversationReplyAttachmentRef[],
    recipients?: EmailRecipients,
    targetMessageId?: number,
    sendFrom?: string,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.approveProposedAction(
      appId,
      actionId,
      clientRequestId,
      attachments,
      recipients,
      targetMessageId,
      sendFrom,
    );
  }

  async dismissProposedAction(
    appId: number,
    actionId: number,
  ): Promise<ProposedAction> {
    return this.repository.dismissProposedAction(appId, actionId);
  }

  async snoozeProposedAction(
    appId: number,
    actionId: number,
    days: number,
  ): Promise<ProposedAction> {
    return this.repository.snoozeProposedAction(appId, actionId, days);
  }

  async editGroupDraft(
    appId: number,
    draftId: number,
    patch: ActionDraftPatch,
  ): Promise<GroupDraft> {
    return this.repository.editGroupDraft(appId, draftId, patch);
  }

  async approveProposedActionsBatch(
    appId: number,
    actionIds: number[],
    clientRequestId: string,
    attachments?: ConversationReplyAttachmentRef[],
    recipients?: EmailRecipients,
    targetMessageId?: number,
    sendFrom?: string,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.approveProposedActionsBatch(
      appId,
      actionIds,
      clientRequestId,
      attachments,
      recipients,
      targetMessageId,
      sendFrom,
    );
  }

  async getConversationMessageEmail(
    appId: number,
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return this.repository.getConversationMessageEmail(appId, messageId);
  }

  async listApplicationReviewGroups(appId: number): Promise<ReviewGroup[]> {
    return this.repository.listApplicationReviewGroups(appId);
  }

  async rejectApplicationReviewGroup(
    appId: number,
    groupId: number,
  ): Promise<void> {
    return this.repository.rejectApplicationReviewGroup(appId, groupId);
  }

  async getEmailLinkBlastRadius(
    appId: number,
    messageId: number,
  ): Promise<{ message_ids: number[] }> {
    return this.repository.getEmailLinkBlastRadius(appId, messageId);
  }

  async markConversationRead(
    appId: number,
    conversationId: number,
    messageIds: number[],
  ): Promise<ConversationUnreadState> {
    return this.repository.markConversationRead(appId, conversationId, messageIds);
  }

  async markConversationUnread(
    appId: number,
    conversationId: number,
  ): Promise<ConversationUnreadState> {
    return this.repository.markConversationUnread(appId, conversationId);
  }

  async detachEmailFromConversation(
    appId: number,
    messageId: number,
  ): Promise<void> {
    return this.repository.detachEmailFromConversation(appId, messageId);
  }

  async attachEmailToConversation(
    appId: number,
    messageId: number,
    conversationId: number,
  ): Promise<void> {
    return this.repository.attachEmailToConversation(
      appId,
      messageId,
      conversationId,
    );
  }

  async replyToConversation(
    appId: number,
    conversationId: number,
    bodyHtml: string,
    attachments: ConversationReplyAttachmentRef[] = [],
    targetMessageId?: number,
    recipients?: ConversationReplyRecipients,
    sendFrom?: string,
    clientRequestId?: string,
  ): Promise<MarketOutboundEmailRead> {
    return this.repository.replyToConversation(
      appId,
      conversationId,
      bodyHtml,
      attachments,
      targetMessageId,
      recipients,
      sendFrom,
      clientRequestId,
    );
  }

  async getConversationReplyDefaults(
    appId: number,
    conversationId: number,
    targetMessageId?: number,
    replyAll = true,
  ): Promise<ConversationReplyDefaults> {
    return this.repository.getConversationReplyDefaults(
      appId,
      conversationId,
      targetMessageId,
      replyAll,
    );
  }

  async getEmailMailTips(
    appId: number,
    recipients: string[],
    sendFrom?: string,
  ): Promise<MailTip[]> {
    return this.repository.getEmailMailTips(appId, recipients, sendFrom);
  }

  async uploadConversationReplyAttachments(
    appId: number,
    conversationId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return this.repository.uploadConversationReplyAttachments(appId, conversationId, formData);
  }

  async listReviewGroups(options: {
    archived?: boolean;
    page?: number;
    perPage?: number;
    search?: string;
    filters?: string;
  }): Promise<CommunicationReviewPage> {
    return this.repository.listReviewGroups(options);
  }

  async getReviewGroup(groupId: number): Promise<ReviewGroup> {
    return this.repository.getReviewGroup(groupId);
  }

  async resolveReviewGroup(
    groupId: number,
    body: ReviewGroupResolveRequest,
  ): Promise<ReviewGroup> {
    return this.repository.resolveReviewGroup(groupId, body);
  }

  async resolveReviewGroupByReference(
    groupId: number,
    conversationReference: string,
  ): Promise<ReviewGroup> {
    return this.repository.resolveReviewGroupByReference(
      groupId,
      conversationReference,
    );
  }

  async dismissReviewGroup(groupId: number): Promise<void> {
    return this.repository.dismissReviewGroup(groupId);
  }

  async archiveReviewGroup(groupId: number): Promise<void> {
    return this.repository.archiveReviewGroup(groupId);
  }

  async unarchiveReviewGroup(groupId: number): Promise<void> {
    return this.repository.unarchiveReviewGroup(groupId);
  }

  async getReviewGroupMemberEmail(
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return this.repository.getReviewGroupMemberEmail(messageId);
  }

  async getMarkets(includeInactive?: boolean): Promise<MarketDirectoryItem[]> {
    return this.repository.getMarkets(includeInactive);
  }

  async updateMarket(
    carrierId: string,
    data: MarketUpdateInput,
  ): Promise<MarketDirectoryItem> {
    return this.repository.updateMarket(carrierId, data);
  }

  async getMarketsDirectory(
    params: MarketsDirectoryParams,
  ): Promise<MarketsDirectoryResponse> {
    return this.repository.getMarketsDirectory(params);
  }

  async setContactActive(contactId: number, archive: boolean): Promise<MarketContact> {
    return this.repository.setContactActive(contactId, archive);
  }

  async createMarket(data: MarketCreateInput): Promise<MarketCreateResponse> {
    return this.repository.createMarket(data);
  }

  async uploadMarketImage(
    carrierId: string,
    file: File,
  ): Promise<MarketDirectoryItem> {
    return this.repository.uploadMarketImage(carrierId, file);
  }

  async deleteMarketImage(carrierId: string): Promise<MarketDirectoryItem> {
    return this.repository.deleteMarketImage(carrierId);
  }

  async getMarketContacts(carrierId: string): Promise<MarketContact[]> {
    return this.repository.getMarketContacts(carrierId);
  }

  async getMarketContactsDirectory(
    params: MarketContactDirectoryParams,
  ): Promise<MarketContactDirectoryResponse> {
    return this.repository.getMarketContactsDirectory(params);
  }

  async createMarketContact(
    carrierId: string,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return this.repository.createMarketContact(carrierId, data);
  }

  async updateMarketContact(
    contactId: number,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return this.repository.updateMarketContact(contactId, data);
  }

  async getMarketFinderMe(): Promise<MarketFinderActorInfo> {
    return this.repository.getMarketFinderMe();
  }

  async searchAimCompanies(q: string, page = 0, size = 20): Promise<AimCompanyPage> {
    return this.repository.searchAimCompanies(q, page, size);
  }

  async getAimCompanyContacts(
    referenceId: number,
    q = "",
    page = 0,
    size = 20,
    carrierId?: string,
  ): Promise<AimContactPage> {
    return this.repository.getAimCompanyContacts(referenceId, q, page, size, carrierId);
  }

  async importAimMarket(data: AimMarketImportInput): Promise<MarketCreateResponse> {
    return this.repository.importAimMarket(data);
  }

  async importAimContacts(
    carrierId: string,
    data: AimContactImportInput,
  ): Promise<AimContactImportResult> {
    return this.repository.importAimContacts(carrierId, data);
  }

  private enrichSubmission<T extends Submission>(submission: T): T {
    return {
      ...submission,
      effective_date: new Date(submission.effective_date),
      created_at: new Date(submission.created_at),
      updated_at: new Date(submission.updated_at),
      archived_at: submission.archived_at
        ? new Date(submission.archived_at)
        : null,
      received_date: submission.received_date
        ? new Date(submission.received_date)
        : null,
    };
  }

  private enrichClassCode(classCode: ScheduleOfHazard): ScheduleOfHazard {
    return {
      ...classCode,
      location_number: classCode.location_number || 1,
      hazard_number: classCode.hazard_number || 0,
      exposure: classCode.exposure || 0,
      premium_basis: classCode.premium_basis || "Payroll",
      exposure_unit: classCode.exposure_unit || null,
    };
  }
}

// Export singleton instances for direct use
export const submissionsService = SubmissionsService.getInstance();
export const marketFinderSubmissionsService =
  SubmissionsService.getMarketFinderInstance();
